import { useEffect, useState } from 'react';
import { saasApi } from '../../lib/api';

const EMPTY_PAYLOAD = `{
  "name": "",
  "description": "",
  "layoutMode": "CONTINUOUS",
  "mediaType": "GAP",
  "stockType": "LABEL",
  "manufacturer": "",
  "model": "",
  "paperWidthMm": 110,
  "paperHeightMm": 50,
  "columnWidthMm": 100,
  "columnHeightMm": 50,
  "widthMm": 100,
  "heightMm": 50,
  "leftMarginMm": 0,
  "topMarginMm": 0,
  "rightMarginMm": 0,
  "bottomMarginMm": 0,
  "pageWidthMm": 110,
  "pageHeightMm": 160,
  "columns": 1,
  "rows": 4,
  "previewRows": 4,
  "horizontalGapMm": 0,
  "verticalGapMm": 0,
  "previewOrder": "ROW_BY_ROW",
  "status": "ACTIVE"
}`;

const EMPTY = {
  slug: '',
  name: '',
  description: '',
  layoutMode: 'CONTINUOUS',
  manufacturer: '',
  model: '',
  paperWidthMm: 110,
  paperHeightMm: 50,
  columnWidthMm: 100,
  columnHeightMm: 50,
  widthMm: 100,
  heightMm: 50,
  pageWidthMm: 110,
  pageHeightMm: 160,
  columns: 1,
  rows: 4,
  previewRows: 4,
  rightMarginMm: 0,
  bottomMarginMm: 0,
  mediaType: 'GAP',
  stockType: 'LABEL',
  featured: false,
  active: true,
  payload: EMPTY_PAYLOAD,
};

function fmtDate(value) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value));
}

function buildPayloadFromForm(form) {
  return {
    slug: form.slug.trim().toLowerCase(),
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    layoutMode: form.layoutMode,
    manufacturer: form.manufacturer.trim() || undefined,
    model: form.model.trim() || undefined,
    paperWidthMm: Number(form.paperWidthMm),
    paperHeightMm: Number(form.paperHeightMm),
    columnWidthMm: Number(form.columnWidthMm),
    columnHeightMm: Number(form.columnHeightMm),
    widthMm: Number(form.columnWidthMm),
    heightMm: Number(form.columnHeightMm),
    pageWidthMm: Number(form.paperWidthMm),
    pageHeightMm: Number(form.paperHeightMm),
    columns: Number(form.columns),
    rows: Number(form.rows),
    previewRows: Number(form.previewRows),
    rightMarginMm: Number(form.rightMarginMm),
    bottomMarginMm: Number(form.bottomMarginMm),
    mediaType: form.mediaType,
    stockType: form.stockType,
    featured: !!form.featured,
    active: !!form.active,
    payload: JSON.parse(form.payload),
  };
}

function toForm(item) {
  return {
    slug: item.slug,
    name: item.name,
    description: item.description ?? '',
    layoutMode: item.layoutMode ?? 'CONTINUOUS',
    manufacturer: item.manufacturer ?? '',
    model: item.model ?? '',
    paperWidthMm: item.paperWidthMm ?? item.pageWidthMm ?? 110,
    paperHeightMm: item.paperHeightMm ?? item.pageHeightMm ?? 50,
    columnWidthMm: item.columnWidthMm ?? item.widthMm,
    columnHeightMm: item.columnHeightMm ?? item.heightMm,
    widthMm: item.columnWidthMm ?? item.widthMm,
    heightMm: item.columnHeightMm ?? item.heightMm,
    pageWidthMm: item.paperWidthMm ?? item.pageWidthMm ?? 110,
    pageHeightMm: item.paperHeightMm ?? item.pageHeightMm ?? 50,
    columns: item.columns,
    rows: item.rows ?? 4,
    previewRows: item.previewRows ?? 4,
    rightMarginMm: item.rightMarginMm ?? 0,
    bottomMarginMm: item.bottomMarginMm ?? 0,
    mediaType: item.mediaType,
    stockType: item.stockType,
    featured: !!item.featured,
    active: !!item.active,
    payload: JSON.stringify(item.payload ?? {}, null, 2),
  };
}

export default function AdminModels() {
  const [models, setModels] = useState([]);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY);

  async function load() {
    setError('');
    try {
      const items = await saasApi.listModels();
      setModels(items);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => {
    load();
  }, []);

  function set(field) {
    return (e) => {
      const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
      setForm((current) => {
        const next = { ...current, [field]: value };
        if (field === 'columnWidthMm') next.widthMm = value;
        if (field === 'columnHeightMm') next.heightMm = value;
        if (field === 'paperWidthMm') next.pageWidthMm = value;
        if (field === 'paperHeightMm') next.pageHeightMm = value;
        return next;
      });
    };
  }

  function startCreate() {
    setEditing(null);
    setForm(EMPTY);
    setCreating(true);
  }

  function startEdit(item) {
    setEditing(item);
    setForm(toForm(item));
    setCreating(true);
  }

  async function submit(e) {
    e.preventDefault();
    setError('');
    try {
      const payload = buildPayloadFromForm(form);
      if (editing) {
        const { slug, ...rest } = payload;
        void slug;
        await saasApi.updateModel(editing.id, rest);
      } else {
        await saasApi.createModel(payload);
      }
      setCreating(false);
      setEditing(null);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  async function deactivate(item) {
    setError('');
    try {
      await saasApi.removeModel(item.id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  }

  return (
    <div className="flex flex-col gap-base">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Modelos</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-2">
            Catálogo global de modelos de formato publicado para todos os tenants.
          </p>
        </div>
        <button
          onClick={startCreate}
          className="rounded-xl bg-primary px-4 py-2 font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors"
        >
          + Novo modelo
        </button>
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}

      {creating && (
        <form onSubmit={submit} className="bg-surface rounded-2xl shadow-sm p-6 space-y-4">
          <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">{editing ? 'Editar modelo' : 'Novo modelo'}</h3>
          <div className="grid gap-4 md:grid-cols-4">
            <Field label="Nome" value={form.name} onChange={set('name')} placeholder="Datamax XL 34x50 3 colunas" />
            <Field label="Slug" value={form.slug} onChange={set('slug')} placeholder="datamax-xl-34x50-3col" disabled={!!editing} />
            <Field label="Fabricante" value={form.manufacturer} onChange={set('manufacturer')} placeholder="Datamax" />
            <Field label="Modelo" value={form.model} onChange={set('model')} placeholder="XL" />
            <Field label="Largura do papel (mm)" type="number" value={form.paperWidthMm} onChange={set('paperWidthMm')} />
            <Field label="Altura do papel/passo (mm)" type="number" value={form.paperHeightMm} onChange={set('paperHeightMm')} />
            <SelectField label="Modo" value={form.layoutMode} onChange={set('layoutMode')} options={['CONTINUOUS', 'PAGE']} />
            <Field label="Colunas" type="number" value={form.columns} onChange={set('columns')} />
            <Field label="Largura da coluna/etiqueta (mm)" type="number" value={form.columnWidthMm} onChange={set('columnWidthMm')} />
            <Field label="Altura da coluna/etiqueta (mm)" type="number" value={form.columnHeightMm} onChange={set('columnHeightMm')} />
            <Field label="Linhas" type="number" value={form.rows} onChange={set('rows')} />
            <Field label="Largura legado página (mm)" type="number" value={form.pageWidthMm} onChange={set('pageWidthMm')} />
            <Field label="Altura legado página (mm)" type="number" value={form.pageHeightMm} onChange={set('pageHeightMm')} />
            <Field label="Linhas de preview" type="number" value={form.previewRows} onChange={set('previewRows')} />
            <Field label="Margem direita (mm)" type="number" value={form.rightMarginMm} onChange={set('rightMarginMm')} />
            <Field label="Margem inferior (mm)" type="number" value={form.bottomMarginMm} onChange={set('bottomMarginMm')} />
            <SelectField label="Mídia" value={form.mediaType} onChange={set('mediaType')} options={['GAP', 'BLACK_MARK', 'CONTINUOUS', 'FANFOLD', 'DIE_CUT', 'TAG', 'RIBBON', 'RFID']} />
            <SelectField label="Estoque" value={form.stockType} onChange={set('stockType')} options={['NONE', 'LABEL', 'TAG', 'RFID', 'FORM']} />
            <div className="grid gap-2">
              <ToggleField label="Modelo em destaque" checked={form.featured} onChange={set('featured')} />
              <ToggleField label="Modelo ativo" checked={form.active} onChange={set('active')} />
            </div>
            <label className="block md:col-span-4">
              <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Descrição</span>
              <textarea
                value={form.description}
                onChange={set('description')}
                rows={2}
                className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
              />
            </label>
            <label className="block md:col-span-4">
              <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">Payload do formato (JSON)</span>
              <textarea
                value={form.payload}
                onChange={set('payload')}
                rows={16}
                className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 font-mono text-[11px] focus:border-primary focus:outline-none"
              />
            </label>
          </div>
          <div className="flex gap-2">
            <button type="submit" className="rounded-xl bg-primary px-4 py-2 font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors">
              Salvar
            </button>
            <button
              type="button"
              onClick={() => {
                setCreating(false);
                setEditing(null);
              }}
              className="rounded-xl px-4 py-2 font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors"
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {models.map((item) => (
          <div key={item.id} className={`bg-surface rounded-2xl shadow-sm p-6 ${!item.active ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between gap-3">
              <h3 className="studio-uniform-title font-h3 text-h3 text-on-surface">{item.name}</h3>
              <div className="flex gap-1.5">
                {item.featured ? <Badge tone="highlight">Destaque</Badge> : null}
                <Badge active={item.active}>{item.active ? 'Ativo' : 'Inativo'}</Badge>
              </div>
            </div>
            <p className="mt-1 text-[10px] text-on-surface-variant">{item.description || 'Sem Descrição.'}</p>
            <ul className="mt-4 space-y-1 text-[11px] text-on-surface-variant">
              <li>{item.manufacturer || 'Sem fabricante'}{item.model ? ` • ${item.model}` : ''}</li>
              <li>{item.paperWidthMm ?? item.pageWidthMm ?? '-'} x {item.paperHeightMm ?? item.pageHeightMm ?? '-'} mm de papel</li>
              <li>{item.columnWidthMm ?? item.widthMm} x {item.columnHeightMm ?? item.heightMm} mm por coluna</li>
              <li>{item.columns} coluna(s)</li>
              <li>{item.mediaType}</li>
            </ul>
            <div className="mt-4 text-[10px] text-on-surface-variant">Atualizado em {fmtDate(item.updatedAt)}</div>
            <div className="mt-4 flex gap-2">
              <button onClick={() => startEdit(item)} className="rounded-lg bg-surface-container-high px-3 py-1.5 text-[11px] font-medium text-on-surface hover:bg-surface-container-highest transition-colors">
                Editar
              </button>
              {item.active && (
                <button onClick={() => deactivate(item)} className="rounded-lg px-3 py-1.5 text-[11px] font-medium text-error hover:bg-error-container/30 transition-colors">
                  Desativar
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Badge({ active, tone, children }) {
  const style =
    tone === 'highlight'
      ? 'bg-primary-container text-on-primary-container'
      : active
        ? 'bg-tertiary-container text-on-tertiary-container'
        : 'bg-surface-container-highest text-on-surface-variant';

  return <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-label-sm ${style}`}>{children}</span>;
}

function Field({ label, ...props }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">{label}</span>
      <input
        {...props}
        className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] focus:border-primary focus:outline-none"
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-on-surface-variant">{label}</span>
      <select value={value} onChange={onChange} className="w-full rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px]">
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}

function ToggleField({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 rounded-lg border border-surface-variant bg-surface-low px-3 py-2 text-[11px] text-on-surface">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
